import React, { useEffect, useReducer, useState } from 'react'
import isFunction from 'lodash/isFunction'
import { PERMISSIONS } from 'src/config/roleConfig'
import {
  useHasRequiredCapabilities,
  useHasRequiredPermissions,
} from 'src/app/AppAuth/AppAuth.hooks'
import {
  StandardButton,
  StandardButtonVariant,
  StandardButtonSize,
} from '../Buttons'
import { ButtonGroup, EditableOnClickContainer } from './Inputs.styles'
import { EditOnClickProps } from './inputs.constants'

export const EditOnClick = ({
  autoWidth,
  isInline,
  editMode,
  onSave,
  onSaveControlClose,
  onCancel,
  isEmpty,
  children,
  requiredPermissions = [PERMISSIONS.READ, PERMISSIONS.EDIT],
  requiredCapabilities,
  resource,
  lastSaveDate,
  onClickConfirmationModal,
  isDisabled,
}: EditOnClickProps): JSX.Element => {
  const [isEditing, toggleEditMode] = useReducer((prev) => !prev, false)
  const [isSaving, setIsSaving] = useState(false)
  const hasPermission_ = useHasRequiredPermissions(requiredPermissions)
  const hasCapabilities_ = useHasRequiredCapabilities(
    requiredCapabilities,
    resource,
  )
  const hasPermissions = requiredCapabilities
    ? hasCapabilities_
    : hasPermission_

  const onSaveFunc: React.MouseEventHandler = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    setIsSaving(true)
    if (isFunction(onSave)) {
      onSave()
      toggleEditMode()
      setIsSaving(false)
    } else if (isFunction(onSaveControlClose)) {
      const close = await onSaveControlClose()
      close && toggleEditMode()
      setIsSaving(false)
    }
  }

  useEffect(() => {
    if (lastSaveDate) {
      toggleEditMode()
    }
  }, [lastSaveDate, toggleEditMode])

  const onCancelFunc = () => {
    isFunction(onCancel) && onCancel()
    toggleEditMode()
  }

  const onClick = () => {
    if (isEditing || isDisabled) {
      return
    }
    if (isFunction(onClickConfirmationModal)) {
      onClickConfirmationModal(() => toggleEditMode())
    } else {
      toggleEditMode()
    }
  }

  return (
    <EditableOnClickContainer
      inline={isInline}
      isEditing={isEditing}
      isEmpty={isEmpty}
      onClick={onClick}
      isDisabled={isDisabled}
      autoWidth={autoWidth}
    >
      {hasPermissions && isEditing && !isDisabled ? (
        <>
          {editMode}
          <ButtonGroup inline={isInline}>
            <StandardButton
              variant={StandardButtonVariant.SECONDARY}
              buttonSize={StandardButtonSize.SMALL}
              onClick={onCancelFunc}
            >
              Cancel
            </StandardButton>
            <StandardButton
              variant={StandardButtonVariant.PRIMARY}
              buttonSize={StandardButtonSize.SMALL}
              onClick={onSaveFunc}
              disabled={isSaving}
            >
              Save
            </StandardButton>
          </ButtonGroup>
        </>
      ) : (
        <>{children}</>
      )}
    </EditableOnClickContainer>
  )
}
